'use client';

import { type ReactNode } from 'react';
import { SModalContainer } from '../SModalContainer';
import { SButton, type SButtonColor } from '../SButton';
import { SIcon, type SIconName } from '../SIcon';
import { STag, type STagColor, type STagShape, type STagSize } from '../STag';
import { cn } from '../../lib/cn';
import { sanitizeInlineHtml } from '../../lib/sanitize-inline-html';

export type SConfirmModalType = 'positive' | 'negative' | 'default';

/** 확인 버튼 프리셋 (sd-confirm-modal ConfirmModalMainButton) */
export type ConfirmModalMainButton =
  'primary_md' | 'primary_outline_md' | 'danger_md' | 'danger_outline_md' | 'neutral_outline_md';

const TYPE_ICON: Record<SConfirmModalType, SIconName | null> = {
  positive: 'notificationOutline',
  negative: 'warningOutline',
  default: null,
};
const TYPE_ICON_COLOR: Record<SConfirmModalType, string> = {
  positive: 'var(--cmp-modal-confirm-positive-icon)',
  negative: 'var(--cmp-modal-confirm-negative-icon)',
  default: '',
};
/** 확인 버튼 프리셋 → SButton color/outline (모두 md 사이즈) */
const MAIN_BUTTON_STYLE: Record<
  ConfirmModalMainButton,
  { color: SButtonColor; outline?: boolean }
> = {
  primary_md: { color: 'primary' },
  primary_outline_md: { color: 'primary', outline: true },
  danger_md: { color: 'danger' },
  danger_outline_md: { color: 'danger', outline: true },
  neutral_outline_md: { color: 'neutral', outline: true },
};
/** type별 기본 확인 버튼 프리셋 (sd-confirm-modal CONFIRM_MODAL_DEFAULT_BUTTON) */
const DEFAULT_MAIN_BUTTON: Record<SConfirmModalType, ConfirmModalMainButton> = {
  positive: 'primary_md',
  negative: 'danger_md',
  default: 'neutral_outline_md',
};

export interface SConfirmModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 백드롭·ESC로 안 닫히고 흔들림 (sd-modal-container persistent). **기본값 false** — 확인 모달은 잃을 입력이 없어 백드롭·ESC로 닫힌다 */
  persistent?: boolean;
  /** 타입 (아이콘·메인버튼 색 결정) */
  type?: SConfirmModalType;
  modalTitle?: string;
  /** 제목 엘리먼트에 추가할 클래스 (sd-confirm-modal titleClass) */
  titleClass?: string;
  /** 콘텐츠 박스 상단 메시지 목록 (각 항목이 한 줄, HTML 문자열 허용 — innerHTML 렌더링) */
  topMessage?: string[];
  /** 콘텐츠 박스 하단 메시지 목록 (각 항목이 한 줄, HTML 문자열 허용 — innerHTML 렌더링) */
  bottomMessage?: string[];
  /** tag 슬롯 기본 태그 레이블 (tagSlot 미지정 시) */
  tagLabel?: string;
  /** 태그 프리셋 (shape/size/color) — sd-confirm-modal tagPreset 대응 */
  tagShape?: STagShape;
  tagSize?: STagSize;
  tagColor?: STagColor;
  /** 콘텐츠 박스에서 태그 오른쪽에 오는 텍스트 (optionSlot 미지정 시) */
  slotLabel?: string;
  /** 콘텐츠 박스 tag 위치 커스텀 콘텐츠 (미지정 시 tagLabel 기반 STag) */
  tagSlot?: ReactNode;
  /** 콘텐츠 박스에서 태그 오른쪽 커스텀 콘텐츠 (미지정 시 slotLabel 텍스트) */
  optionSlot?: ReactNode;
  /** 콘텐츠 박스 바로 아래에 통으로 들어가는 커스텀 콘텐츠 (전체 폭) */
  contentSlot?: ReactNode;
  /** 메인 버튼 */
  mainButtonLabel?: string;
  /** 확인 버튼 프리셋 (없으면 type에 따라 자동 결정) */
  mainButtonName?: ConfirmModalMainButton;
  /** 서브(취소) 버튼 */
  subButtonLabel?: string;
  /** 확인 버튼 클릭 (sdOk) */
  onOk?: () => void;
  onCancel?: () => void;
  /** 닫기(X) 버튼 클릭 (sdClose) */
  onClose?: () => void;
}

/** SConfirmModal — sd-confirm-modal 포팅. 아이콘+제목+메시지+태그+확인/취소 버튼. */
export function SConfirmModal({
  open,
  onOpenChange,
  persistent = false,
  type = 'positive',
  modalTitle = '',
  titleClass,
  topMessage = [],
  bottomMessage = [],
  tagLabel,
  tagShape = 'square',
  tagSize = 'sm',
  tagColor = 'grey',
  slotLabel,
  tagSlot,
  optionSlot,
  contentSlot,
  mainButtonLabel = '확인',
  mainButtonName,
  subButtonLabel,
  onOk,
  onCancel,
  onClose,
}: SConfirmModalProps) {
  const iconName = TYPE_ICON[type];
  const mainStyle = MAIN_BUTTON_STYLE[mainButtonName ?? DEFAULT_MAIN_BUTTON[type]];

  // 콘텐츠 박스 안에서 tag(왼쪽) / option(오른쪽) — 각각 커스텀 슬롯(우선) 또는 기본 fallback
  const tagNode =
    tagSlot ??
    (tagLabel ? <STag shape={tagShape} size={tagSize} color={tagColor} label={tagLabel} /> : null);
  const optionNode = optionSlot ?? (slotLabel ? <span>{slotLabel}</span> : null);
  const hasContentBox = tagNode != null || optionNode != null;
  const hasBody =
    topMessage.length > 0 || bottomMessage.length > 0 || hasContentBox || contentSlot != null;

  return (
    <SModalContainer
      open={open}
      onOpenChange={onOpenChange}
      persistent={persistent}
      ariaTitle={modalTitle}
      showClose
      onClose={onClose}
      // min-w는 화면이 그보다 좁을 땐 양보해야 한다 — min-width가 max-width를 이기므로
      // min()으로 컨테이너의 화면 폭 클램프와 같은 값까지만 내려가게 한다
      className="w-fit min-w-[min(520px,calc(100dvw-48px))]"
    >
      {/* 카드가 화면 높이에 걸려 줄어들면 본문(아이콘·제목·메시지)만 스크롤되고 버튼 행은 남는다 */}
      <div className="flex min-h-0 flex-auto flex-col items-center gap-[40px] px-[32px] py-[40px]">
        <div className="flex w-full min-h-0 flex-col items-center gap-[20px] overflow-y-auto">
          {/* 아이콘 → 제목 간격 12 (title-gap) */}
          <div className="flex flex-col items-center gap-[12px]">
            {iconName && <SIcon name={iconName} size={32} color={TYPE_ICON_COLOR[type]} />}
            <h2
              className={cn(
                'text-center text-[18px] font-bold leading-[30px] text-[color:var(--cmp-modal-confirm-title-color)]',
                titleClass,
              )}
            >
              {modalTitle}
            </h2>
          </div>

          {hasBody && (
            <div className="flex flex-col items-center gap-[20px] text-center text-[12px] font-normal leading-[20px] text-[color:var(--cmp-modal-confirm-message-color)] w-full">
              {/* 각 줄은 HTML 문자열을 허용하되, sanitizeInlineHtml 로 살균 후 innerHTML 렌더 (XSS 방어) */}
              {topMessage.length > 0 && (
                <div className="flex flex-col gap-[2px]">
                  {topMessage.map((m, i) => (
                    <p
                      key={i}
                      className="m-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(m) }}
                    />
                  ))}
                </div>
              )}
              {hasContentBox && (
                // content-box: 흰 박스 + 1px 테두리 + 패딩 + 14/700 (원본 sd-confirm-modal__content-box)
                // tag 왼쪽, slotLabel/optionSlot 이 그 오른쪽에 gap 8 로 나란히 놓인다
                <div className="flex items-center justify-center gap-[8px] rounded-[8px] border border-solid border-[color:var(--color-grey-30)] bg-white px-[16px] py-[12px] text-[14px] font-bold leading-[22px] w-full">
                  {tagNode}
                  {optionNode}
                </div>
              )}
              {/* content-box 바로 아래 통 슬롯 — 박스 스타일 없이 전체 폭으로 소비자 콘텐츠를 그대로 렌더 */}
              {contentSlot != null && <div className="w-full">{contentSlot}</div>}
              {bottomMessage.length > 0 && (
                <div className="flex flex-col gap-[2px]">
                  {bottomMessage.map((m, i) => (
                    <p
                      key={i}
                      className="m-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(m) }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 gap-[var(--cmp-modal-confirm-button-gap)]">
          {subButtonLabel && (
            <SButton
              color="neutral"
              outline
              size="md"
              onClick={() => {
                onCancel?.();
                onOpenChange?.(false);
              }}
              label={subButtonLabel}
            />
          )}
          <SButton
            {...mainStyle}
            size="md"
            onClick={() => {
              onOk?.();
              onOpenChange?.(false);
            }}
            label={mainButtonLabel}
          />
        </div>
      </div>
    </SModalContainer>
  );
}
